import React, { createContext, useContext } from "react";
import {
  FundRecommendationResponse,
  FundResponse,
} from "@rentablo/api/recommender";
import {
  FundDetails,
  Investment,
  QuoteV2,
  useGetQuotesV2,
} from "@rentablo/api/apiv1";
import { RecommendationLogo } from "@features/recommender/common";
import { useShowDetailedFundByIsin } from "@rentablo/api/apiv1";

export interface RecommendationDetailsContextProps {
  originalFund?: FundResponse;
  recommendation: FundRecommendationResponse;
  investment?: Investment;
  fundDetails?: FundDetails;
  investmentFundDetails?: FundDetails;
  quote?: QuoteV2;
  imageUrl: string;
  names: {
    recommendation: string;
    investment: string;
  };
  logos: {
    originalFund?: React.ReactNode;
    recommendation?: React.ReactNode;
  };
  isLoading: boolean;
}

const RecommendationDetailsContext =
  createContext<RecommendationDetailsContextProps>({
    originalFund: undefined,
    recommendation: {} as FundRecommendationResponse,
    investment: undefined,
    fundDetails: undefined,
    investmentFundDetails: undefined,
    quote: undefined,
    imageUrl: "",
    names: {
      recommendation: "",
      investment: "",
    },
    logos: {
      originalFund: undefined,
      recommendation: undefined,
    },
    isLoading: false,
  });

export const useRecommendationDetails = (): RecommendationDetailsContextProps =>
  useContext(RecommendationDetailsContext);

const RecommendationDetailsProvider = ({
  originalFund,
  recommendation,
  images,
  children,
  investment,
}: {
  originalFund?: FundResponse;
  recommendation: FundRecommendationResponse;
  images: Record<string, string | null | undefined>;
  investment?: Investment;
  children: React.ReactNode;
}) => {
  const { data: fundDetails, isLoading: isFundFetching } =
    useShowDetailedFundByIsin(
      {
        isin: recommendation.fund.isin,
        include: ["breakdowns", "fundamentals"],
      },
      {
        enabled: Boolean(recommendation.fund.isin),
      }
    );

  const { data: investmentFundDetails, isLoading: isInvestmentFundFetching } =
    useShowDetailedFundByIsin(
      {
        isin: originalFund?.isin!,
        include: ["breakdowns", "fundamentals"],
      },
      {
        enabled: Boolean(originalFund?.isin),
      }
    );

  const { data: quoteList, isLoading: isQuoteFetching } = useGetQuotesV2(
    {
      securityId: recommendation.fund.isin ? [recommendation.fund.isin] : [],
      quoteProvider: "ariva",
      optimal: true,
    },
    {
      enabled: Boolean(recommendation.fund.isin),
    }
  );

  const quote = quoteList?.quotes?.[0];

  const imageUrl = images?.[recommendation.fund.isin] ?? "";
  const investmentImageUrl = images?.[investment?.isin ?? ""] ?? "";

  const logos = {
    originalFund: (
      <RecommendationLogo
        imageUrl={investmentImageUrl}
        alt={investment?.name}
        size={28}
      />
    ),
    recommendation: (
      <RecommendationLogo
        imageUrl={imageUrl}
        alt={recommendation.fund.name}
        size={28}
      />
    ),
  };

  const names = {
    recommendation:
      fundDetails?.standardisedName ?? recommendation.fund.name ?? "",
    investment:
      investmentFundDetails?.standardisedName ?? investment?.name ?? "",
  };

  return (
    <RecommendationDetailsContext.Provider
      value={{
        originalFund,
        recommendation,
        investment,
        fundDetails,
        investmentFundDetails,
        quote,
        imageUrl,
        logos,
        names,
        isLoading:
          isFundFetching || isQuoteFetching || isInvestmentFundFetching,
      }}
    >
      {children}
    </RecommendationDetailsContext.Provider>
  );
};

export { RecommendationDetailsProvider };
